/**
 * ToastProvider — transient corner notifications, in the Field Day language.
 *
 * The portal had no toast system; this mirrors the LeaveGuard provider shape
 * (context + createPortal into <body>) so it composes the same way and lives
 * at the app root. Toasts auto-dismiss after a per-toast duration and can
 * carry one action — when the action is a download link it renders a real
 * <a download>, which is the guaranteed manual path if a programmatic
 * auto-download was blocked by the browser.
 *
 * First consumer: the SAT lead magnet (a "you got the Free SAT Playbook"
 * congrats fired right after OTP verification). Keep it generic — it's a
 * plain success-toast surface, not SAT-specific.
 */
import * as React from "react";
import { createPortal } from "react-dom";
import "../styles/toast-field.css";

export interface ToastAction {
  label: string;
  /** When set, the action is a download anchor (most reliable manual path). */
  href?: string;
  /** Suggested filename for the download anchor. */
  download?: string;
  onClick?: () => void;
}

export interface ToastInput {
  /** IntegralCF headline. */
  title: string;
  /** Optional supporting line. */
  body?: string;
  /** Small uppercase eyebrow (e.g. "Congrats"). */
  eyebrow?: string;
  /** Tilted yellow sticker text (e.g. "Free"). */
  sticker?: string;
  /** Optional single action (download link or button). */
  action?: ToastAction;
  /** Auto-dismiss after this many ms. Default 8000; pass 0 to disable. */
  duration?: number;
}

interface Toast extends ToastInput {
  id: number;
}

interface ToastContextValue {
  show(input: ToastInput): number;
  dismiss(id: number): void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION = 8000;

export function ToastProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const idRef = React.useRef(0);
  const timersRef = React.useRef<Map<number, number>>(new Map());

  const dismiss = React.useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const show = React.useCallback(
    (input: ToastInput) => {
      const id = ++idRef.current;
      setToasts((list) => [...list, { ...input, id }]);
      const duration = input.duration ?? DEFAULT_DURATION;
      if (duration > 0) {
        const timer = window.setTimeout(() => dismiss(id), duration);
        timersRef.current.set(id, timer);
      }
      return id;
    },
    [dismiss],
  );

  // Clear any pending timers on unmount.
  React.useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      timers.clear();
    };
  }, []);

  const ctx = React.useMemo<ToastContextValue>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      {toasts.length > 0 &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="toast-field__stack" aria-live="polite" aria-atomic="false">
            {toasts.map((t) => (
              <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within <ToastProvider>");
  }
  return ctx;
}

/**
 * Non-throwing variant — returns null when rendered outside <ToastProvider>.
 * The provider is app-wide in production (App.tsx), so this is null only in
 * isolated component/route tests; callers no-op the toast in that case.
 */
export function useToastOptional(): ToastContextValue | null {
  return React.useContext(ToastContext);
}

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: () => void;
}): React.ReactElement {
  const { title, body, eyebrow, sticker, action } = toast;
  return (
    <div className="toast-field__card" role="status">
      {sticker && <span className="toast-field__sticker">{sticker}</span>}
      <button type="button" className="toast-field__close" aria-label="Dismiss" onClick={onDismiss}>
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="18" y1="6" x2="6" y2="18" />
        </svg>
      </button>

      <div className="toast-field__head">
        {eyebrow && <span className="toast-field__eyebrow">{eyebrow}</span>}
        <p className="toast-field__title">{title}</p>
        {body && <p className="toast-field__body">{body}</p>}
      </div>

      {action &&
        (action.href ? (
          <a
            className="toast-field__action"
            href={action.href}
            download={action.download ?? true}
            onClick={action.onClick}
          >
            {action.label}
            <span className="toast-field__arc" aria-hidden="true">
              <ToastArrow />
            </span>
          </a>
        ) : (
          <button type="button" className="toast-field__action" onClick={action.onClick}>
            {action.label}
            <span className="toast-field__arc" aria-hidden="true">
              <ToastArrow />
            </span>
          </button>
        ))}
    </div>
  );
}

function ToastArrow(): React.ReactElement {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
